// Instances
import { DishBackgroundVideo } from "./background.instance";
import { toast } from "react-toastify";

// Tanslations
import i18n from 'i18next';

// Types
import { ConnectDeviceOptions } from "../dish.types";

/**
 * Device Filter Type
 * @type DeviceFilterType
 */
export type DeviceFilterType = {
    audio?: boolean;
    video?: boolean | {
        pan: true, tilt: true, zoom: true
    };
}

/**
 * Device Instance
 * @class DeviceInstance
 */
export class DeviceInstance {

    dishBackgroundVideoInstance: DishBackgroundVideo | undefined;

    constructor() {
      console.log(`[DeviceInstance] constructor`)
    }

    async stopBackground() {
        try {
            if (this.dishBackgroundVideoInstance) {
                console.log("Stopping background video instance");
                this.dishBackgroundVideoInstance.stop();
                this.dishBackgroundVideoInstance = undefined;
            }
        } catch (error) {
            console.error("Error stopping background:", error);
        }
    }

    async replaceBackground(stream: MediaStream, image: string) {
        // First, make sure any existing background instance is stopped
        await this.stopBackground();

        return new Promise<MediaStream>((resolve, reject) => {
            // create image html
            const background = document.createElement('img');
            let backgroundAdded = false;

            const cleanupBackground = () => {
                if (background && backgroundAdded && background.parentNode) {
                    background.parentNode.removeChild(background);
                    backgroundAdded = false;
                }
            };

            background.src = image;
            // set in body
            document.body.appendChild(background);
            backgroundAdded = true;

            // position fixed zindex:1000
            background.style.position = 'fixed';
            background.style.top = '0';
            background.style.left = '0';
            background.style.zIndex = '-1000';
            // pointer events none
            background.style.pointerEvents = 'none';
            // hide
            background.style.opacity = '0';

            // Handle image load errors
            background.onerror = () => {
                cleanupBackground();
                console.error('Failed to load background image');
                // If image fails, return original stream instead of rejecting
                resolve(stream);
            };

            background.onload = async () => {
                // Let's ensure the image is fully loaded with a reasonable size
                if (background.width === 0 || background.height === 0) {
                    cleanupBackground();
                    console.error('Background image has invalid dimensions');
                    resolve(stream);
                    return;
                }

                try {
                    // Wait for model to initialize
                    setTimeout(async () => {
                        try {
                            const segmentation = new DishBackgroundVideo({ stream, background });
                            this.dishBackgroundVideoInstance = segmentation;
                            segmentation.run();

                            // Add a timeout to handle cases where getStream might hang
                            const timeoutPromise = new Promise<MediaStream>((_, reject) => {
                                setTimeout(() => reject(new Error('Background processing timeout')), 5000);
                            });

                            try {
                                const streamResult = await Promise.race([
                                    segmentation.getStream(),
                                    timeoutPromise
                                ]);

                                // Set up proper cleanup for the stream
                                const cleanupHandler = () => {
                                    console.log("Stream ended or inactive, cleaning up background");
                                    if (this.dishBackgroundVideoInstance === segmentation) {
                                        this.stopBackground();
                                    }
                                };

                                // Clean up on stream end
                                stream.addEventListener('inactive', cleanupHandler);
                                stream.addEventListener('ended', cleanupHandler);

                                // Add cleanup handler to result stream too
                                streamResult.addEventListener('inactive', cleanupHandler);
                                streamResult.addEventListener('ended', cleanupHandler);

                                resolve(streamResult);
                            } catch (error) {
                                console.error('Failed to get processed stream:', error);
                                if (this.dishBackgroundVideoInstance === segmentation) {
                                    this.stopBackground();
                                }
                                cleanupBackground();
                                // Fallback to original stream
                                resolve(stream);
                            }
                        } catch (initError) {
                            console.error('Error initializing background processing:', initError);
                            cleanupBackground();
                            resolve(stream);
                        }
                    }, 500);
                } catch (error) {
                    console.error('Error in background processing:', error);
                    cleanupBackground();
                    resolve(stream);
                }
            };
        });
    }

    async getFilterStream(options: MediaStreamConstraints) {

        try {

          const defaultConstraints: MediaStreamConstraints = {
            video: {
              frameRate: { min: 5, ideal: 10, max: 15 },
              width: { min: 640, ideal: 1280, max: 1920 },
              height: { min: 480, ideal: 720, max: 1080 },
            }
          }

          // If the options contain a video device, we need to add the video constraints
          if (
           options.video && typeof options.video === 'object' &&
           defaultConstraints.video && typeof defaultConstraints.video === 'object'
          ) {
            options.video = {
              ...defaultConstraints.video,
              ...options.video,
            }
          }

          const localStream = await navigator.mediaDevices.getUserMedia(options);
          return localStream;

        }
        catch (e) {

            console.error('ERROR -> getDefaultDevice', e);

        }

    }


    /**
     * Retrieves the default media device (audio and video) stream.
     *
     * This function attempts to get the user's media devices with specific constraints
     * for audio and video. If a filter is provided, it checks if any of the audio or video
     * device labels match the filter. If a match is found, it logs an error and shows a warning
     * message indicating that the device is not valid.
     *
     * @param {string[]} [filter] - An optional array of device labels to filter out. If any of the
     * device labels match the filter, the function will return `undefined` and show a warning message.
     *
     * @returns {Promise<MediaStream | undefined>} - A promise that resolves to the media stream if successful,
     * or `undefined` if an error occurs or a filtered device is found.
     */
    async getDefaultDevice(filter?: string[]): Promise<MediaStream | undefined> {
      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                frameRate: 10,
                sampleRate: 44100,
                // @ts-ignore
                suppressLocalAudioPlayback: true,
            },
            video: {
                frameRate: { min: 5, ideal: 10, max: 15 },
                echoCancellation: true,
                noiseSuppression: true,
                sampleRate: 44100,
                width: { min: 320, ideal: 640, max: 1280 }, // min: 320, ideal: 640, max: 1280
                height: { min: 240, ideal: 480, max: 720 }, // min: 240, ideal: 480, max: 720
                // @ts-ignore
                zoom: true,
                pan: true,
                tilt: true,
            }
        });

        const audioTracks = localStream.getAudioTracks();
        const videoTracks = localStream.getVideoTracks();

        // Comprobar si alguno de los labels de audio o video coincide con los filtros
        if (filter) {
          console.log('DEBUG -> getDefaultDevice', 'Filter', filter);

          const audioFilter = audioTracks.find((audioTrack) => filter.includes(audioTrack.label));
          const videoFilter = videoTracks.find((videoTrack) => filter.includes(videoTrack.label));

          // Si encontramos un filtro, es que se usa un dispositivo que no se debe usar
          // Por lo que se debe mostrar un mensaje de error para informar de la
          // incompatibilidad del dispositivo
          if (audioFilter) {
            console.error('ERROR -> getDefaultDevice', 'Audio device not available', audioFilter.label);
            toast.warn(i18n.t('warn.device.audio.not.valid'));
            return undefined;
          }

          if (videoFilter) {
            console.error('ERROR -> getDefaultDevice', 'Video device not available', videoFilter.label);
            toast.warn(i18n.t('warn.device.video.not.valid'));
            return undefined;
          }
        }

        return localStream;
      }
      catch (e) {
        console.error('ERROR -> getDefaultDevice', e);
        toast.info(i18n.t('info.device.not.available'));
      }

      return undefined;
    }

    async getDevices(filter?: DeviceFilterType) {

        const devices = await navigator.mediaDevices.enumerateDevices();
        const filteredDevices = devices.filter((device) => {

            if (filter) {

                if (filter.audio && device.kind === 'audioinput') {

                    return true;

                }
                if (filter.video && device.kind === 'videoinput') {

                    return true;

                }

            }
            return false;

        });
        return filteredDevices;

    }

    async getDeviceMedia(devices: { videoId: string } | { audioId: string } | { videoId: string, audioId: string }, options?: ConnectDeviceOptions) {
        // First stop any existing background processing to prevent resource leaks
        if (options?.background) {
            await this.stopBackground();
        }

        let deviceAudio: MediaDeviceInfo | undefined = undefined
        let deviceVideo: MediaDeviceInfo | undefined = undefined
        if ('videoId' in devices) {

            // find device of video or audio
            const videoDevices = await this.getDevices({ video: true, });
            deviceVideo = videoDevices.find((device) => device.deviceId === devices.videoId);
            if (!deviceVideo) {

                throw new Error('Device not found');

            }

        }
        if ('audioId' in devices) {

            // find device of video or audio
            const audioDevices = await this.getDevices({ audio: true });
            deviceAudio = audioDevices.find((device) => device.deviceId === devices.audioId);
            if (!deviceAudio) {

                throw new Error('Device not found');

            }

        }

        // if is audio
        if ('audioId' in devices && 'videoId' in devices) {

            const localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: devices.audioId,
                    noiseSuppression: options?.noise,
                    echoCancellation: options?.echo,
                    frameRate: 10
                },
                video: {
                    deviceId: devices.videoId ? { exact: devices.videoId } : undefined,
                    frameRate: { min: 5, ideal: 10, max: 15 },
                    noiseSuppression: options?.noise,
                    echoCancellation: options?.echo,
                    width: { min: 640, ideal: 1280, max: 1920 },
                    height: { min: 480, ideal: 720, max: 1080 },
                    // @ts-ignore
                    pan: true,
                    tilt: true,
                    zoom: true

                },
            });

            console.log('localStream', localStream.getAudioTracks()[0]);
            console.log('localStream', localStream.getVideoTracks()[0]);

            if (options?.background) {
                try {
                    const streamBackground = await this.replaceBackground(localStream, options.background);
                    return streamBackground;
                } catch (error) {
                    console.error("Error applying background:", error);
                    return localStream;
                }
            }
            else return localStream;

        }
        else if (deviceAudio && 'audioId' in devices) {

            const localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: devices.audioId,
                    noiseSuppression: options?.noise,
                    echoCancellation: options?.echo,
                },
                video: false,
            });

            console.log('localStream', localStream.getAudioTracks()[0]);
            console.log('localStream', localStream.getVideoTracks()[0]);

            return localStream;

        }
        if (deviceVideo && deviceVideo.kind === 'videoinput' && 'videoId' in devices && deviceAudio === undefined) {

            try {

                const localStream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        deviceId: devices.videoId,
                        frameRate: { min: 5, ideal: 10, max: 15 },
                        echoCancellation: options?.echo,
                        noiseSuppression: options?.noise,

                        // min: SD, ideal: HD, max: Full HD
                        width: { min: 640, ideal: 1280, max: 1920 },
                        height: { min: 480, ideal: 720, max: 1080 },

                        // @ts-ignore
                        pan: true,
                        tilt: true,
                        zoom: true

                    },
                });

                if (options?.background) {
                    try {
                        return await this.replaceBackground(localStream, options.background);
                    } catch (error) {
                        console.error("Error applying background:", error);
                        return localStream;
                    }
                }
                else return localStream;

            }
            catch (e) {

                console.error("Error getting video device:", e);
                throw e;

            }

        }

    }

    async getScreenMedias() {

        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                frameRate: { ideal: 10, max: 24 },
                width: { ideal: 1280, max: 1920 },
                height: { ideal: 720, max: 1080 },
            },
            audio: true,
        });

        return stream;

    }

    /**
     * Asynchronously requests permissions for audio and video media devices.
     *
     * @returns {Promise<{ audio: boolean, video: boolean, media: boolean }>}
     * A promise that resolves to an object indicating the permission status for audio, video, and media.
     *
     * @example
     * const permissions = await DeviceInstance.getPermissions();
     * console.log(permissions); // { audio: false, video: false, media: false }
     */
    static async getPermissions(): Promise<{ audio: boolean, video: boolean, media: boolean }> {

      const permissions = {
          audio: false,
          video: false,
          media: false,
      };

      // @ts-ignore
      await navigator.getUserMedia(
        {
          video: {
            pan: true,
            tilt: true,
            zoom: true,
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 },
          },
        },
        function (stream: MediaStream) {
          stream.getTracks().forEach((x: any) => x.stop());
        },
        (err: any) => console.log(err)
      );

      // @ts-ignore
      await navigator.getUserMedia({ audio: true }, function (stream) {

        stream.getTracks().forEach((x: any) => x.stop());

      }, (err: any) => console.log(err));

      return permissions;

    }

    log(message: any, data?: any) {

    }

    async getAudioOutput() {

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutput = devices.filter((device) => device.kind === 'audiooutput');
        return audioOutput;

    }

    async getAudioInput() {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInput = devices.filter((device) => device.kind === 'audioinput');
      return audioInput;
    }

    async getVideoInput() {

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInput = devices.filter((device) => device.kind === 'videoinput');
        return videoInput;

    }
}
